import { Ear, Compass, Handshake } from "lucide-react";
import { motion } from "framer-motion";
import { usePageMeta } from "../hooks/usePageMeta";
import { PageHeader } from "../components/PageHeader";
import { CtaBanner } from "../components/CtaBanner";
import { Reveal, fadeUp, stagger } from "../lib/motion";

const VALORI = [
  {
    icon: Ear,
    titolo: "Ascolto",
    testo: "Ogni percorso parte da una conversazione: prima di proporre, vogliamo capire.",
  },
  {
    icon: Compass,
    titolo: "Orientamento",
    testo: "Ti aiutiamo a vedere le opzioni con chiarezza e a scegliere in modo consapevole.",
  },
  {
    icon: Handshake,
    titolo: "Accompagnamento",
    testo: "Restiamo al tuo fianco durante tutto il percorso, passo dopo passo.",
  },
];

export default function ChiSiamo() {
  usePageMeta(
    "Chi siamo",
    "Forma Futuro è un centro studi che si occupa di orientamento e formazione: diplomi, lauree, formazione professionale e certificazioni."
  );

  return (
    <main>
      <PageHeader
        testId="page-chi-siamo"
        eyebrow="Chi siamo"
        title="Persone che aiutano persone a crescere."
        description="Forma Futuro è un centro studi che si occupa di orientamento e formazione, con un approccio concreto e umano."
      />

      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <Reveal>
              <p className="font-serif-editorial italic text-2xl sm:text-3xl text-navy leading-snug">
                "Crediamo che ogni persona abbia un percorso possibile davanti
                a sé. Il nostro lavoro è aiutarla a vederlo e a percorrerlo."
              </p>
              <div className="mt-8 space-y-5 text-base text-slate-600 leading-relaxed">
                <p>
                  Centro Studi Forma Futuro nasce per rispondere a un'esigenza
                  semplice: orientarsi tra diplomi, lauree, corsi di formazione
                  e certificazioni non è facile, e farlo da soli può scoraggiare.
                  Noi mettiamo al centro la persona, il suo tempo e i suoi
                  obiettivi.
                </p>
                <p>
                  Ci occupiamo di orientamento e di percorsi di formazione a
                  360 gradi: dal conseguimento del diploma ai percorsi
                  universitari, dalla formazione professionale alle
                  certificazioni linguistiche e informatiche. In ogni ambito, il
                  nostro ruolo è lo stesso: ascoltare, orientare, accompagnare.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.15} className="relative">
              <div className="rounded-[2rem] overflow-hidden border-8 border-white shadow-[0_40px_80px_-40px_rgba(8,42,89,0.45)]">
                <img
                  src="/assets/chi-siamo.jpg"
                  alt="Gli spazi del centro studi"
                  className="w-full h-[360px] sm:h-[480px] object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-6 w-36 h-36 rounded-full border border-dashed border-azure-mid spin-slower pointer-events-none" />
            </Reveal>
          </div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {VALORI.map((v, i) => (
              <motion.div
                key={v.titolo}
                variants={fadeUp}
                data-testid={`valore-${i}`}
                className="bg-white rounded-[1.6rem] border border-slate-200 p-8 transition-all duration-300 hover:-translate-y-1 hover:border-azure hover:shadow-[0_28px_60px_-30px_rgba(8,42,89,0.3)]"
              >
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-azure-light text-azure mb-6">
                  <v.icon size={22} strokeWidth={1.8} />
                </span>
                <h3 className="text-lg font-bold text-navy">{v.titolo}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                  {v.testo}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <CtaBanner
        title="Vieni a conoscerci."
        text="Il modo migliore per capire come lavoriamo è parlarne: richiedi un colloquio di orientamento."
      />
    </main>
  );
}
